// @/components/reusable/chart-type-toggle.jsx
// V2 Chart Type Toggle - Reusable toggle for switching between chart views

import React from 'react';
import clsx from 'clsx';
import { motion } from 'motion/react';

// ============================================================================
// ICONS
// ============================================================================

// Vertical bars icon - bars pointing upwards (like a bar chart)
const VerticalBarsIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 20.25v-12m6 12v-8.25m6 8.25V10.5m6 9.75V6.75" />
  </svg>
);

// Horizontal bars icon - stacked horizontal bars of different lengths
const HorizontalBarsIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h12M3.75 17.25h8.25" />
  </svg>
);

// Globe/World map icon - for geography charts
const GlobeIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
  </svg>
);

// ============================================================================
// CHART TYPE TOGGLE
// ============================================================================

/**
 * ChartTypeToggle - V2 reusable toggle for switching between chart views
 *
 * Features:
 * - Animated sliding indicator
 * - Consistent with v2 design system ring/shadow styles
 * - Vertical/Horizontal bar chart icons
 *
 * @param {string} chartType - Current chart type: 'vertical' | 'horizontal'
 * @param {Function} onChangeChartType - Callback when chart type changes
 * @param {string} className - Additional classes for the container
 */
const ChartTypeToggle = ({ chartType, onChangeChartType, className }) => (
  <div className={clsx(
    'relative isolate inline-flex rounded-lg ring-1 ring-inset ring-zinc-300 shadow-xs overflow-hidden',
    className
  )}>
    <motion.div
      className="absolute inset-y-0 w-1/2 bg-zinc-900 rounded-lg"
      initial={false}
      animate={{ x: chartType === 'vertical' ? 0 : '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    />
    <button
      type="button"
      onClick={() => onChangeChartType('vertical')}
      className={clsx(
        'relative z-10 inline-flex items-center justify-center size-8 rounded-l-lg transition-colors duration-150 cursor-pointer',
        chartType === 'vertical' ? 'text-white' : 'text-zinc-600 hover:text-zinc-900'
      )}
      title="Vertical bar chart"
    >
      <VerticalBarsIcon className="size-4" />
    </button>
    <button
      type="button"
      onClick={() => onChangeChartType('horizontal')}
      className={clsx(
        'relative z-10 inline-flex items-center justify-center size-8 rounded-r-lg transition-colors duration-150 cursor-pointer',
        chartType === 'horizontal' ? 'text-white' : 'text-zinc-600 hover:text-zinc-900'
      )}
      title="Horizontal bar chart"
    >
      <HorizontalBarsIcon className="size-4" />
    </button>
  </div>
);

// Export icons for custom use cases
ChartTypeToggle.VerticalBarsIcon = VerticalBarsIcon;
ChartTypeToggle.HorizontalBarsIcon = HorizontalBarsIcon;
ChartTypeToggle.GlobeIcon = GlobeIcon;

export { ChartTypeToggle, VerticalBarsIcon, HorizontalBarsIcon, GlobeIcon };
export default ChartTypeToggle;
